export type IncomeDistributionSource = 'fixed' | 'extra';

export type IncomeDistributionRule = Readonly<{
  id: string;
  goalId: string;
  source: IncomeDistributionSource | 'both';
  kind: 'fixed-amount' | 'percentage';
  /** Minor units for fixed amounts; basis points for percentages (100 = 1%). */
  value: number;
  currency: string;
}>;

export type IncomeDistributionAllocation = Readonly<{
  goalId: string;
  amountMinorUnits: number;
}>;

export type IncomeDistributionGoal = Readonly<{
  id: string;
  remainingMinorUnits: number;
}>;

export function validateIncomeDistributionRules(
  rules: readonly IncomeDistributionRule[],
): readonly IncomeDistributionRule[] {
  const ids = new Set<string>();
  for (const rule of rules) {
    if (
      !rule.id?.trim() ||
      !rule.goalId?.trim() ||
      ids.has(rule.id) ||
      !['fixed', 'extra', 'both'].includes(rule.source) ||
      !['fixed-amount', 'percentage'].includes(rule.kind) ||
      !Number.isSafeInteger(rule.value) ||
      rule.value <= 0 ||
      (rule.kind === 'percentage' && rule.value > 10_000) ||
      !/^[A-Z]{3}$/.test(rule.currency)
    ) {
      throw new Error('Invalid income distribution rule.');
    }
    ids.add(rule.id);
  }
  for (const source of ['fixed', 'extra'] as const) {
    const percentage = rules
      .filter(rule => rule.kind === 'percentage' && appliesTo(rule, source))
      .reduce((sum, rule) => sum + rule.value, 0);
    if (percentage > 10_000) {
      throw new Error('Income distribution percentages must not exceed 100%.');
    }
  }
  return rules;
}

export function suggestIncomeDistribution({
  rules,
  source,
  amountMinorUnits,
  currency,
  goals,
}: {
  rules: readonly IncomeDistributionRule[];
  source: IncomeDistributionSource;
  amountMinorUnits: number;
  currency: string;
  goals: readonly IncomeDistributionGoal[];
}): readonly IncomeDistributionAllocation[] {
  validateIncomeDistributionRules(rules);
  assertMinorUnits(amountMinorUnits);
  const remaining = new Map(
    goals.map(goal => [goal.id, goal.remainingMinorUnits]),
  );
  const amounts = new Map<string, number>();
  for (const rule of rules) {
    const capacity = remaining.get(rule.goalId);
    if (
      capacity === undefined ||
      capacity <= 0 ||
      rule.currency !== currency ||
      !appliesTo(rule, source)
    ) {
      continue;
    }
    assertMinorUnits(capacity);
    // Use the original income for every percentage and round down to cents.
    const requested =
      rule.kind === 'percentage'
        ? Number((BigInt(amountMinorUnits) * BigInt(rule.value)) / 10_000n)
        : rule.value;
    const amount = Math.min(requested, capacity);
    if (amount > 0) {
      amounts.set(rule.goalId, (amounts.get(rule.goalId) ?? 0) + amount);
      remaining.set(rule.goalId, capacity - amount);
    }
  }
  return [...amounts].map(([goalId, amount]) => ({
    goalId,
    amountMinorUnits: amount,
  }));
}

export function validateIncomeDistribution(
  incomeMinorUnits: number,
  allocations: readonly IncomeDistributionAllocation[],
  goals: readonly IncomeDistributionGoal[],
): number {
  assertMinorUnits(incomeMinorUnits);
  const capacities = new Map(
    goals.map(goal => [goal.id, goal.remainingMinorUnits]),
  );
  const ids = new Set<string>();
  let available = incomeMinorUnits;
  for (const allocation of allocations) {
    assertMinorUnits(allocation.amountMinorUnits);
    const capacity = capacities.get(allocation.goalId);
    if (
      allocation.amountMinorUnits === 0 ||
      capacity === undefined ||
      allocation.amountMinorUnits > capacity ||
      allocation.amountMinorUnits > available ||
      ids.has(allocation.goalId)
    ) {
      throw new Error(
        'Income distribution exceeds the income or goal capacity.',
      );
    }
    ids.add(allocation.goalId);
    available -= allocation.amountMinorUnits;
  }
  return available;
}

function appliesTo(
  rule: IncomeDistributionRule,
  source: IncomeDistributionSource,
): boolean {
  return rule.source === 'both' || rule.source === source;
}

function assertMinorUnits(value: number): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(
      'Income distribution amounts must be non-negative safe integers.',
    );
  }
}
